<script setup>
import { computed, onMounted, ref, watch, shallowRef } from 'vue';
import { useStore } from 'vuex';
import { OnClickOutside } from '@vueuse/components';
import DocumentPreview from './components/DocumentPreview.vue';
import Pane from '@/dashboard/components/Pane.vue';
import FullWidthPaginationFooter from './components/FullWidthPaginationFooter.vue';
import LoadingIndicator from '@/shared/components/LoadingIndicator.vue';
import DropdownMenu from '@/dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Dialog from '@/dashboard/components-next/dialog/Dialog.vue';
import DataGrid from '@/dashboard/components-next/data-grid/DataGrid.vue';
import {
  isImmutableSchemaKnowledgebase,
  filterSpaceKbSchemaProperties,
} from './composables/useImmutableSchemaKnowledgebase';

const props = defineProps({
  knowledgebase: {
    type: Object,
    default: () => ({}),
  },
  filteredDocuments: {
    type: Array,
    default: () => [],
  },
  visibleColumns: {
    type: Array,
    default: () => [],
  },
  documentsMeta: {
    type: Object,
    default: () => ({
      page: 1,
      per_page: 20,
      total: 0,
      total_pages: 0,
    }),
  },
  isSchemaActive: {
    type: Boolean,
    default: false,
  },
  activeFilters: {
    type: Object,
    default: () => ({}),
  },
  onRecordClick: {
    type: Function,
    default: null,
  },
  processingCount: {
    type: Number,
    default: 0,
  },
  processingRecords: {
    type: Array,
    default: () => [],
  },
  errorCount: {
    type: Number,
    default: 0,
  },
  errorRecords: {
    type: Array,
    default: () => [],
  },
  isUploading: {
    type: Boolean,
    default: false,
  },
  uploadProgress: {
    type: Number,
    default: 0,
  },
  uploadStats: {
    type: Object,
    default: () => ({ total: 0, completed: 0, failed: 0, inProgress: 0 }),
  },
});

const emit = defineEmits([
  'selectionChanged',
  'pageChange',
  'schemaChange',
  'updateDetected',
  'toggleSchema',
  'retryFailedRecords',
  'deleteProcessingRecords',
  'deleteFailedRecords',
  'openSitemaps',
  'columnDragStart',
  'columnDragEnd',
  'showColumn',
  'columnFilter',
]);

const store = useStore();

const showSchemaToggle = computed(
  () => !isImmutableSchemaKnowledgebase(props.knowledgebase)
);

const dataGridRef = ref(null);
const previousDocuments = ref(new Map());
const addFieldButtonRef = ref(null);
const newFieldDialogRef = ref(null);
const newFieldName = ref('');
const newFieldType = ref('string');
const newFieldDescription = ref('');
const showAddFieldDropdown = ref(false);
const selectedDocument = ref(null);
const isPaginationChange = ref(false);

const documentToRowData = document => {
  const data = document.metadata || {};
  data.id = document.id;
  data.record = document?.record;
  data.content = document.content;

  return data;
};

const DEFAULT_WIDTHS = {
  id: 120,
  record: 200,
  content: 400,
};

// Add schema change handler
const handleSchemaChange = async event => {
  const { action, payload } = event;

  try {
    const currentSchema = store.getters['knowledgebase/getActiveSchema'];
    let updatedSchema = { ...currentSchema };

    switch (action) {
      case 'addProperty':
        if (!updatedSchema.properties[payload.field]) {
          updatedSchema.properties[payload.field] = {
            type: payload.type,
            description: `${payload.field} field`,
          };
          emit('schemaChange', {
            action: 'add',
            field: payload.field,
            type: payload.type,
          });
        }
        break;

      case 'deleteProperty':
        if (
          updatedSchema.properties[payload.field] &&
          !['id', 'content'].includes(payload.field)
        ) {
          delete updatedSchema.properties[payload.field];
          emit('schemaChange', { action: 'delete', field: payload.field });
        }
        break;

      case 'changeType':
        if (updatedSchema.properties[payload.field]) {
          updatedSchema.properties[payload.field].type = payload.type;
          emit('schemaChange', {
            action: 'changeType',
            field: payload.field,
            type: payload.type,
          });
        }
        break;

      case 'renameProperty':
        if (
          updatedSchema.properties[payload.oldField] &&
          !updatedSchema.properties[payload.newField]
        ) {
          updatedSchema.properties[payload.newField] = {
            ...updatedSchema.properties[payload.oldField],
          };
          delete updatedSchema.properties[payload.oldField];
          emit('schemaChange', {
            action: 'rename',
            oldField: payload.oldField,
            newField: payload.newField,
          });
        }
        break;

      default:
        break;
    }

    await store.dispatch('knowledgebase/updateActiveSchema', {
      id: props.knowledgebase.id,
      schema: updatedSchema,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error updating schema:', error);
  }
};

const schema = computed(() => {
  const data = store.getters['knowledgebase/getActiveSchema'];
  return data;
});

const rowData = shallowRef([]);
const selectedRows = ref([]);
const isInitialLoad = ref(true);

const isLoading = computed(() => store.state.documents.uiFlags.isFetching);
const isUpdating = computed(() => store.state.documents.uiFlags.isUpdating);
const isSchemaLoading = computed(
  () => store.state.knowledgebase.uiFlags.isFetching
);
const loadedKnowledgebaseId = computed(
  () => store.state.documents.uiFlags.loadedKnowledgebaseId
);

const hasLoadedCurrentKnowledgebase = computed(
  () => loadedKnowledgebaseId.value === props.knowledgebase?.id
);

const shouldShowLoading = computed(
  () => isLoading.value && isInitialLoad.value
);

const hasDocument = computed(() => !!selectedDocument.value);

function capitalizeHeader(header) {
  return header.charAt(0).toUpperCase() + header.slice(1);
}

function isFieldEditable(fieldName) {
  const nonEditableFields = ['id', 'record'];

  if (nonEditableFields.includes(fieldName)) {
    return false;
  }

  if (
    fieldName.startsWith('ROOKOO_INTERNAL_') ||
    fieldName.startsWith('INTERNAL_ROOKOO_')
  ) {
    return false;
  }

  return true;
}

const schemaProperties = computed(() =>
  filterSpaceKbSchemaProperties(schema.value?.properties, props.knowledgebase)
);

const columns = computed(() => {
  if (!schemaProperties.value) return [];

  const cols = Object.keys(schemaProperties.value).map(propertyKey => ({
    headerName: capitalizeHeader(propertyKey),
    field: propertyKey,
    editable: isFieldEditable(propertyKey),
    width: DEFAULT_WIDTHS[propertyKey] || undefined,
    type: schemaProperties.value[propertyKey]?.type || 'string',
  }));

  const idColumn = cols.find(col => col.field === 'id');
  const otherColumns = cols.filter(col => col.field !== 'id');

  const recordColumn = {
    headerName: 'Source',
    field: 'record',
    editable: false,
    width: DEFAULT_WIDTHS.record,
    type: 'object',
  };

  return [idColumn, recordColumn, ...otherColumns].filter(Boolean);
});

const hiddenColumns = computed(() => {
  if (!columns.value || columns.value.length === 0) return [];
  if (!props.visibleColumns || props.visibleColumns.length === 0) return [];

  return columns.value.filter(col => !props.visibleColumns.includes(col.field));
});

const addFieldMenuItems = computed(() => {
  const items = [
    {
      label: 'New Field',
      value: 'new-field',
      action: 'new-field',
      icon: 'i-lucide-plus-circle',
    },
  ];

  if (hiddenColumns.value.length > 0) {
    items.push(
      ...hiddenColumns.value.map(col => ({
        label: col.headerName,
        value: col.field,
        action: 'show-column',
        icon: 'i-lucide-eye',
      }))
    );
  }

  return items;
});

const isGeoField = field => {
  if (!schema.value?.properties) return false;
  return schema.value.properties[field]?.type === 'geo';
};

const formatGeoValue = value => {
  if (!value) return '';

  // Handle object format: { lat: number, lng: number } or { lat: number, lon: number }
  if (typeof value === 'object') {
    const lat = value.lat || value.latitude;
    const lng = value.lng || value.lon || value.longitude;
    if (lat !== undefined && lng !== undefined) {
      return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
    }
  }

  // Handle string format: "lat,lng" or "lat, lng"
  if (typeof value === 'string') {
    const parts = value.split(',').map(p => p.trim());
    if (parts.length === 2) {
      return `${Number(parts[0]).toFixed(6)}, ${Number(parts[1]).toFixed(6)}`;
    }
  }

  return String(value);
};

const getOpenStreetMapUrl = value => {
  if (!value) return '#';

  let lat;
  let lng;

  // Handle object format
  if (typeof value === 'object') {
    lat = value.lat || value.latitude;
    lng = value.lng || value.lon || value.longitude;
  }

  // Handle string format: "lat,lng" or "lat, lng"
  if (typeof value === 'string') {
    const parts = value.split(',').map(p => p.trim());
    if (parts.length === 2) {
      lat = parts[0];
      lng = parts[1];
    }
  }

  if (lat !== undefined && lng !== undefined) {
    return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=15/${lat}/${lng}`;
  }

  return '#';
};

const formatCellValue = (value, field) => {
  if (value === null || value === undefined) return '';
  if (field === 'record') {
    if (typeof value === 'object') {
      return value.name || value.title || 'Source';
    }
    return value;
  }
  if (isGeoField(field)) {
    return formatGeoValue(value);
  }
  return typeof value === 'string' ? value : String(value);
};

const getCellType = field => {
  if (!schema.value?.properties) return 'string';
  return schema.value.properties[field]?.type || 'string';
};

const isCellEditableCheck = (rowId, field) => {
  const cellType = getCellType(field);
  const complexTypes = ['geo', 'object', 'array'];
  if (complexTypes.includes(cellType)) return false;
  return isFieldEditable(field);
};

const handleCellEdit = ({ rowId, field, newValue }) => {
  const row = rowData.value.find(r => r.id === rowId);
  if (!row) return;

  // Convert boolean string values to actual booleans
  let processedValue = newValue;
  const cellType = getCellType(field);
  if (cellType === 'bool' || cellType === 'boolean') {
    processedValue = newValue === 'true' || newValue === true;
  }

  // Update local data
  const rowIndex = rowData.value.findIndex(r => r.id === rowId);
  if (rowIndex !== -1) {
    rowData.value[rowIndex] = {
      ...rowData.value[rowIndex],
      [field]: processedValue,
    };
  }

  // Handle content field update in store
  if (field === 'content') {
    store.dispatch('documents/update', {
      knowledgebaseId: props.knowledgebase.id,
      documentId: rowId,
      content: processedValue,
    });
  } else {
    // Handle metadata field update in store
    const metadata = {};
    metadata[field] = processedValue;

    store.dispatch('documents/update', {
      knowledgebaseId: props.knowledgebase.id,
      documentId: rowId,
      metadata: metadata,
    });
  }

  // Update our local tracking
  const currentDocs = props.filteredDocuments || [];
  const docIndex = currentDocs.findIndex(doc => doc.id === rowId);
  if (docIndex !== -1) {
    const updatedDoc = { ...currentDocs[docIndex] };
    if (field === 'content') {
      updatedDoc.content = processedValue;
    } else {
      updatedDoc.metadata = { ...updatedDoc.metadata, [field]: processedValue };
    }
    previousDocuments.value.set(rowId, updatedDoc);
  }

  // Notify parent that an update occurred
  emit('updateDetected');
};

const handleSelectionChanged = selected => {
  selectedRows.value = selected;
  const allSelected = selected.length === rowData.value.length;
  emit('selectionChanged', selected, allSelected);
};

const handleAddFieldAction = ({ action, value }) => {
  showAddFieldDropdown.value = false;

  if (action === 'new-field') {
    newFieldDialogRef.value?.open();
  } else if (action === 'show-column') {
    emit('showColumn', value);
  }
};

const handleCreateField = async () => {
  if (!newFieldName.value.trim()) return;

  const payload = {
    field: newFieldName.value,
    type: newFieldType.value,
    description: newFieldDescription.value || `${newFieldName.value} field`,
  };

  await handleSchemaChange({ action: 'addProperty', payload });

  await store.dispatch(
    'knowledgebase/fetchActiveSchema',
    props.knowledgebase.id
  );

  newFieldName.value = '';
  newFieldType.value = 'string';
  newFieldDescription.value = '';
  newFieldDialogRef.value?.close();
};

const updateDocumentTracking = () => {
  const currentDocs = props.filteredDocuments || [];
  previousDocuments.value.clear();
  currentDocs.forEach(doc => {
    previousDocuments.value.set(doc.id, { ...doc });
  });
};

const applyDocumentChanges = newDocuments => {
  if (!newDocuments || !Array.isArray(newDocuments)) return;

  // Don't apply changes if documents don't belong to current knowledgebase
  if (!hasLoadedCurrentKnowledgebase.value) return;

  const currentSelection = selectedRows.value.map(row => row.id);
  const currentDocs = new Map(newDocuments.map(doc => [doc.id, doc]));
  const previousDocs = previousDocuments.value;

  let hasChanges = false;

  currentDocs.forEach((doc, id) => {
    if (!previousDocs.has(id)) {
      hasChanges = true;
    } else {
      const prevDoc = previousDocs.get(id);
      const docChanged = JSON.stringify(doc) !== JSON.stringify(prevDoc);
      const recordStatusChanged = doc.record?.status !== prevDoc.record?.status;
      if (docChanged || recordStatusChanged) {
        hasChanges = true;
      }
    }
  });

  previousDocs.forEach((doc, id) => {
    if (!currentDocs.has(id)) {
      hasChanges = true;
    }
  });

  if (hasChanges) {
    rowData.value = newDocuments.map(documentToRowData);
    updateDocumentTracking();

    if (currentSelection.length > 0) {
      setTimeout(() => {
        const newSelection = rowData.value.filter(row =>
          currentSelection.includes(row.id)
        );
        selectedRows.value = newSelection;
      }, 10);
    }
  }
};

function onSelectionChanged(selected, allSelected) {
  emit('selectionChanged', selected, allSelected);
}

const handlePreview = async row => {
  const documents = store.getters['documents/getDocuments'];
  const foundDocument = documents.find(doc => {
    return doc.id === row.id;
  });

  selectedDocument.value = foundDocument;
};

const closePreview = () => {
  selectedDocument.value = null;
};

const removeDocumentsByRecordId = recordId => {
  const rowsToRemove = rowData.value.filter(row => row.record?.id === recordId);

  if (rowsToRemove.length > 0) {
    rowData.value = rowData.value.filter(row => row.record?.id !== recordId);

    rowsToRemove.forEach(row => {
      previousDocuments.value.delete(row.id);
    });
  }
};

// Expose methods so parent can call them
defineExpose({
  handlePreview,
  removeDocumentsByRecordId,
});

const handlePageChange = newPage => {
  // Mark that the next document change is due to pagination
  isPaginationChange.value = true;
  emit('pageChange', newPage);
};

const initGrid = async (forceFullReload = false) => {
  const documents = props.filteredDocuments || [];
  const activeSchema = store.getters['knowledgebase/getActiveSchema'];

  // Don't initialize if documents don't belong to current knowledgebase
  if (!hasLoadedCurrentKnowledgebase.value && documents.length > 0) {
    return;
  }

  if (activeSchema) {
    if (!forceFullReload && previousDocuments.value.size > 0) {
      applyDocumentChanges(documents);
    } else {
      rowData.value = documents.map(documentToRowData);
      updateDocumentTracking();
      isInitialLoad.value = false;
    }
  }
};

onMounted(() => {
  initGrid();
});

watch(schema, () => {
  if (
    schema.value &&
    hasLoadedCurrentKnowledgebase.value &&
    rowData.value.length === 0
  ) {
    initGrid();
  }
});

// Clear grid immediately when switching knowledgebases to prevent flash of old data
watch(
  () => props.knowledgebase?.id,
  (newId, oldId) => {
    if (newId && oldId && newId !== oldId) {
      // Clear all grid state immediately
      rowData.value = [];
      previousDocuments.value.clear();
      selectedRows.value = [];
      isInitialLoad.value = true;

      // Notify parent that selection changed
      emit('selectionChanged', [], false);
    }
  }
);

watch(
  () => props.filteredDocuments,
  (newDocuments, oldDocuments) => {
    // Don't process documents if they don't match current knowledgebase
    const storeKbId = loadedKnowledgebaseId.value;
    const currentKbId = props.knowledgebase?.id;

    if (storeKbId !== currentKbId && newDocuments?.length > 0) {
      return;
    }

    if (isPaginationChange.value) {
      isPaginationChange.value = false;
      initGrid(true);
      return;
    }

    if (oldDocuments && oldDocuments.length > 0) {
      applyDocumentChanges(newDocuments);
    } else {
      initGrid();
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col relative">
    <!-- Update Loading Indicator (positioned overlay) -->
    <LoadingIndicator
      v-if="isUpdating"
      message="Saving changes..."
      position="fixed"
      placement="bottom-right"
      variant="primary"
      size="sm"
      :backdrop="false"
      :z-index="60"
    />

    <!-- Schema Loading Indicator (positioned overlay) -->
    <LoadingIndicator
      v-if="isSchemaLoading"
      message="Loading schema..."
      position="fixed"
      placement="bottom-right"
      variant="secondary"
      size="sm"
      :backdrop="false"
      :z-index="65"
    />

    <!-- Table View -->
    <div class="flex-1 flex flex-col min-h-0">
      <!-- Main Loading State for Initial Load -->
      <div
        v-if="shouldShowLoading && !rowData.length"
        class="flex-1 flex items-center justify-center"
      >
        <LoadingIndicator
          message="Loading documents..."
          variant="primary"
          size="md"
          position="inline"
        />
      </div>

      <!-- Grid with data -->
      <template v-else>
        <DataGrid
          ref="dataGridRef"
          class="flex-1 min-h-0"
          :columns="columns"
          :rows="rowData"
          :visible-columns="visibleColumns"
          :selected-rows="selectedRows"
          :format-cell-value="formatCellValue"
          :get-cell-type="getCellType"
          :is-cell-editable="isCellEditableCheck"
          fill-height
          :active-filters="activeFilters"
          @update:selected-rows="handleSelectionChanged"
          @cell-edit="handleCellEdit"
          @column-hide="emit('showColumn', $event)"
          @column-drag-start="emit('columnDragStart')"
          @column-drag-end="emit('columnDragEnd')"
          @column-filter="emit('columnFilter', $event)"
        >
          <!-- Header Actions Slot -->
          <template #header-actions>
            <OnClickOutside
              v-if="showSchemaToggle"
              @trigger="showAddFieldDropdown = false"
            >
              <button
                ref="addFieldButtonRef"
                type="button"
                class="inline-flex items-center gap-1 px-2 py-1 text-xs text-woot-600 hover:text-woot-700 hover:bg-woot-50 rounded transition-colors"
                @click="showAddFieldDropdown = !showAddFieldDropdown"
              >
                <span class="i-lucide-plus w-3.5 h-3.5" />
                Add Field
              </button>

              <div
                v-if="showAddFieldDropdown"
                class="absolute top-full left-4 mt-1 z-50"
              >
                <DropdownMenu
                  :menu-items="addFieldMenuItems"
                  @action="handleAddFieldAction"
                />
              </div>
            </OnClickOutside>
          </template>

          <!-- Custom Cell Rendering for Geo Fields -->
          <template #cell="{ row, field, value, editing }">
            <template v-if="!editing && isGeoField(field)">
              <a
                v-if="value"
                :href="getOpenStreetMapUrl(value)"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-1 text-woot-600 hover:text-woot-700 hover:underline w-full min-w-0"
                @click.stop
              >
                <span class="i-lucide-map-pin w-3.5 h-3.5 flex-shrink-0" />
                <span class="truncate">{{ formatGeoValue(value) }}</span>
              </a>
              <span v-else class="text-slate-400">—</span>
            </template>
          </template>
        </DataGrid>

        <!-- Pagination for Table View with Schema Toggle - Always at bottom -->
        <FullWidthPaginationFooter
          class="flex-shrink-0"
          :current-page="documentsMeta.current_page"
          :total-items="documentsMeta.total"
          :items-per-page="documentsMeta.per_page"
          :is-schema-active="isSchemaActive"
          :show-schema-toggle="showSchemaToggle"
          :processing-count="processingCount"
          :processing-records="processingRecords"
          :error-count="errorCount"
          :error-records="errorRecords"
          :is-uploading="isUploading"
          :upload-progress="uploadProgress"
          :upload-stats="uploadStats"
          @update:current-page="handlePageChange"
          @toggle-schema="emit('toggleSchema')"
          @retry-failed-records="emit('retryFailedRecords', $event)"
          @delete-processing-records="emit('deleteProcessingRecords', $event)"
          @delete-failed-records="emit('deleteFailedRecords', $event)"
          @open-sitemaps="emit('openSitemaps')"
        />
      </template>
    </div>

    <Pane v-model:show="hasDocument" :on-close="closePreview">
      <DocumentPreview
        :document="selectedDocument"
        :schema="schema"
        :knowledgebase="knowledgebase"
      />
    </Pane>

    <!-- New Field Dialog -->
    <Dialog
      ref="newFieldDialogRef"
      title="Create New Field"
      description="Add a new field to your knowledge base schema"
      confirm-button-label="Create Field"
      :disable-confirm-button="!newFieldName.trim()"
      width="md"
      @confirm="handleCreateField"
    >
      <div class="flex flex-col gap-4">
        <!-- Field Name -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Field Name
          </label>
          <input
            v-model="newFieldName"
            type="text"
            placeholder="e.g., category, tags, priority"
            class="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white text-n-slate-12"
          />
        </div>

        <!-- Field Type -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Field Type
          </label>
          <select
            v-model="newFieldType"
            class="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white text-n-slate-12"
          >
            <option value="string">Text</option>
            <option value="integer">Integer</option>
            <option value="float">Float</option>
            <option value="boolean">Boolean</option>
            <option value="array">Array</option>
            <option value="object">Object</option>
          </select>
        </div>

        <!-- Field Description -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Description (Optional)
          </label>
          <textarea
            v-model="newFieldDescription"
            rows="3"
            placeholder="Describe what this field is used for"
            class="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white text-n-slate-12 resize-none"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>
